import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrencyINR, formatDateDDMMYYYY } from "./calculations";
import LogoPicture from "../assets/Logopdf.png"
import Rules1 from "../assets/Rules1.png"
import Rules2 from "../assets/Rules2.png"
import Rules3 from "../assets/Rules3.png"

import CustomerRules from "../assets/Rules.jpeg"

export function generateBillingInvoicePdf(options) {
  console.log("options", options);
  const {
    billingId,
    branchName,
    customer = {},
    loan = {},
    goldRate,
    ornamentImage,
    ornaments = [],
    totals = {},
  } = options;

  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Theme colors mapped from src/assets/Logopdf.png
  const colors = {
    primary: [176, 196, 112],       // Premium Black from logo background
    secondary: [26, 26, 26],   // Gold from logo text
    accent: [226, 230, 157],     // Pale yellow from logo bottom background
    textPrimary: [26, 26, 26],   // Dark text
    textSecondary: [82, 82, 91], // Gray text
    border: [229, 231, 235],     // Light border
    surface: [250, 250, 250],    // Light surface
    sectionTitleFill: [200, 204, 214], // Section title bar background (stronger grey)
  };

  const renderPage = (copyType, isFirstPage) => {
    if (!isFirstPage) {
      doc.addPage();
    }

    let y = 14;

    // --- Header Section ---
    // Logo
    try {
      doc.addImage(LogoPicture, "PNG", 14, y - 4, 40, 15);
    } catch (e) {
      console.log("Logo error", e);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...colors.primary);
      doc.text("MAPS ASSOCIATES", 14, y + 6);
    }

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.textSecondary);
    doc.text("No 2, First floor, Joseph Complex,", 14, y + 15);
    doc.text("Xaviers colony, Tirunelveli - 627005", 14, y + 19);
    doc.text("Contact: 04623597793", 14, y + 23);
    doc.text("E-mail: tvl@mapsassociates.com", 14, y + 27);

    // Center Column
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...colors.textPrimary);
    doc.text("MAP'S GOLD FINANCE", pageW / 2, y + 2, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Reg No: TN2020251230107", pageW / 2, y + 8, { align: "center" });

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.textSecondary);
    doc.text(copyType, pageW - 14, y + 2, { align: "right" });
    doc.setFont("helvetica", "normal");

    y += 8;

    // Invoice Details
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    const dateStr = new Date().toLocaleDateString("en-IN");
    const dateStrWidth = doc.getTextWidth(dateStr);
    
    doc.setTextColor(...colors.textPrimary);
    doc.text(`Date:`, pageW - 14 - dateStrWidth - 2, y, { align: "right" });
    doc.setTextColor(...colors.textSecondary);
    doc.text(dateStr, pageW - 14, y, { align: "right" });

    y += 24;

    // Decorative line
    doc.setDrawColor(...colors.accent);
    doc.setLineWidth(0.5);
    doc.line(14, y, pageW - 14, y);

    y += 4;

    // --- Customer & Loan Details Section ---
    
    // 1) First Row Layout: Billed To & Profile Picture
    const totalWidth = pageW - 28;
    const picWidth = 24; // Tight fit for the reduced image column
    const billedToWidth = totalWidth - picWidth;

    const billedToData = [
      [ 
        { content: "Customer Details:", colSpan: 2, styles: { fontStyle: 'bold', fontSize: 9, textColor: colors.secondary, fillColor: colors.sectionTitleFill } },
        { content: "", rowSpan: 5, styles: { halign: 'center', valign: 'middle' } } // Image cell
      ],
      [ "Name:", customer.customerName || "—" ],
      [ "Customer ID:", customer.customerId || "—" ],
      [ "Address:", customer.address || "—" ],
      [ "Phone:", `+91 ${customer.phone || "—"}` ]
    ];

    autoTable(doc, {
      startY: y,
      body: billedToData,
      theme: 'grid',
      styles: {
        cellPadding: 1.5,
        fontSize: 8,
        textColor: colors.textSecondary,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: colors.textPrimary, cellWidth: billedToWidth * 0.3 },
        1: { cellWidth: billedToWidth * 0.7 },
        2: { cellWidth: picWidth }
      },
      margin: { left: 14 },
      tableWidth: totalWidth,
      didDrawCell: function(data) {
        if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
          if (customer.profilePicture) {
            try {
              let format = 'JPEG';
              const match = customer.profilePicture.match(/data:image\/([a-zA-Z]+);base64/);
              if (match && match[1]) {
                format = match[1].toUpperCase();
                if (format === 'JPG') format = 'JPEG';
              }
              const picSize = 22; // Reduced size to fit tighter layout
              const xPos = data.cell.x + (data.cell.width - picSize) / 2; // Center horizontally
              const yPos = data.cell.y + (data.cell.height - picSize) / 2; // Center vertically within spanned row height
              doc.addImage(customer.profilePicture, format, xPos, yPos, picSize, picSize);
            } catch (e) { console.error("Error drawing customer profile", e); }
          }
        }
      }
    });

    y = doc.lastAutoTable.finalY + 4;

    // 2) Second Row Layout: Loan Information & Interest & Repayment
    const halfWidth = totalWidth / 2;
    const loanStatusColor = loan.isClosing ? [220, 38, 38] : [22, 163, 74];
    const gstText = loan.includeGst ? " (+ GST)" : "";

    const totalNetNum = Number(totals.totalOrnamentNetGrams);
    const totalNetDisplay = Number.isFinite(totalNetNum)
      ? `${Number(totalNetNum.toFixed(3)).toString()} g`
      : "0 g";

    const loanInfoData = [
      ["Loan Status:", { content: loan.isClosing ? "Closed" : "Active", styles: { textColor: loanStatusColor, fontStyle: 'bold' } }],
      ["Loan Number:", billingId || "—"],
      ["Loan Amount:", formatCurrencyINR(loan.loanAmount)],
      ["Loan Date:", formatDateDDMMYYYY(loan.startDate)],
      ["Due Date:", formatDateDDMMYYYY(loan.endDate)],
    ];
    
    const interestInfoData = [
      ["Interest Rate:", `${loan.interest || "—"}% p.a.`],
      ["Tenure:", `${loan.tenure || "—"} Days`],
      ...(loan.processingFeeAmount !== undefined && loan.processingFeeAmount !== null
          ? [["Process. Fee:", `${formatCurrencyINR(loan.processingFeeAmount)}${gstText}`]]
          : []),
      ["Total Net (g):", totalNetDisplay],
    ];

    const maxRows = Math.max(loanInfoData.length, interestInfoData.length);
    const row2TableData = [
      [
        { content: "Loan Information:", colSpan: 2, styles: { fontStyle: 'bold', fontSize: 9, textColor: colors.secondary, fillColor: colors.sectionTitleFill } },
        { content: "Interest & Repayment:", colSpan: 2, styles: { fontStyle: 'bold', fontSize: 9, textColor: colors.secondary, fillColor: colors.sectionTitleFill } }
      ]
    ];
    
    for (let i = 0; i < maxRows; i++) {
      const loanItem = loanInfoData[i] || ["", ""];
      const intItem = interestInfoData[i] || ["", ""];
      row2TableData.push([...loanItem, ...intItem]);
    }

    autoTable(doc, {
      startY: y,
      body: row2TableData,
      theme: 'grid',
      styles: {
        cellPadding: 1.5,
        fontSize: 8,
        textColor: colors.textSecondary,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: colors.textPrimary, cellWidth: halfWidth * 0.35 },
        1: { cellWidth: halfWidth * 0.65 },
        2: { fontStyle: 'bold', textColor: colors.textPrimary, cellWidth: halfWidth * 0.35 },
        3: { cellWidth: halfWidth * 0.65 }
      },
      margin: { left: 14 },
      tableWidth: totalWidth,
    });

    y = doc.lastAutoTable.finalY + 6;

    // --- Ornaments Table ---
    const tableBody = ornaments.map((o, idx) => [
      String(idx + 1),
      o.description || "—",
      String(o.units || "—"),
      String(o.gross || "—"),
      String(o.net || "—"),
    ]);

    const ornPicWidth = totalWidth * 0.25; 
    const ornTableWidth = totalWidth;

    autoTable(doc, {
      startY: y,
      head: [
        [
          { content: "Ornaments Details", colSpan: 5, styles: { fontStyle: 'bold', fontSize: 9, textColor: colors.secondary, fillColor: colors.sectionTitleFill, halign: 'left' } },
          { content: "", rowSpan: tableBody.length + 2, styles: { fillColor: [255, 255, 255], halign: 'center', valign: 'middle' } }
        ],
        ["S.No", "Description", "Units", "Gross (g)", "Net (g)"]
      ],
      body: tableBody,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3,
        textColor: colors.textPrimary,
        lineColor: colors.border,
        lineWidth: 0.1,
        valign: 'middle'
      },
      headStyles: {
        fillColor: colors.sectionTitleFill,
        textColor: colors.secondary,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
        1: { halign: 'center' },
        2: { halign: 'center', cellWidth: 25 }, 
        3: { halign: 'center', cellWidth: 25 },
        4: { halign: 'center', cellWidth: 25 },
        5: { cellWidth: ornPicWidth }
      },
      margin: { left: 14 },
      tableWidth: ornTableWidth,
      didDrawCell: function(data) {
        if (data.section === 'head' && data.column.index === 5 && data.row.index === 0) {
          if (ornamentImage) {
            try {
              let format = 'JPEG';
              const match = ornamentImage.match(/data:image\/([a-zA-Z]+);base64/);
              if (match && match[1]) {
                format = match[1].toUpperCase();
                if (format === 'JPG') format = 'JPEG';
              }
              const imgWidth = data.cell.width - 2; 
              const imgHeight = data.cell.height - 2;
              const xPos = data.cell.x + 1; 
              const yPos = data.cell.y + 1; 
              doc.addImage(ornamentImage, format, xPos, yPos, imgWidth, imgHeight);
            } catch (e) { console.error("Error drawing ornament image", e); }
          }
        }
      }
    });

    const finalYOrnaments = doc.lastAutoTable?.finalY || y + 40;

    // --- Summary & Terms Section ---
    const summaryWouldStartAt = finalYOrnaments + 6;
    let sumY = summaryWouldStartAt;

    if (sumY + 60 > pageH) {
      doc.addPage();
      sumY = 15;
    }

    const summaryHalfWidth = totalWidth / 2;
    const interestVal = loan.interest || 24;
    const effectiveRate = (interestVal - 2.1).toFixed(1);
    
    // Left side: Summary Data
    const summaryRows = [
      [
        { content: "Summary", colSpan: 2, styles: { fontStyle: 'bold', fontSize: 8, textColor: colors.secondary, fillColor: colors.sectionTitleFill } }
      ],
      ["Loan Amount:", formatCurrencyINR(loan.loanAmount)],
      ["Processing Fees:", formatCurrencyINR(loan.processingFeeAmount || 0)],
    ];
    
    if (loan.includeGst) {
      summaryRows.push(["GST:", formatCurrencyINR(loan.gstAmount || 0)]);
    }
    
    summaryRows.push([
      { content: "Net Loan Amount:", styles: { fontStyle: 'bold', textColor: colors.secondary } },
      { content: formatCurrencyINR(loan.netLoanAmount || loan.loanAmount), styles: { fontStyle: 'bold', textColor: colors.secondary, halign: 'right' } }
    ]);

    autoTable(doc, {
      startY: sumY,
      body: summaryRows,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: { top: 0.8, bottom: 0.8, left: 2, right: 2 },
        minCellHeight: 0,
        textColor: colors.textSecondary,
        lineColor: colors.border,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: colors.textPrimary, cellWidth: summaryHalfWidth * 0.4 },
        1: { cellWidth: summaryHalfWidth * 0.6, halign: 'right' }
      },
      margin: { left: 14 },
      tableWidth: summaryHalfWidth,
    });

    // Right side: Terms — top-align with summary when it stays on same page
    const rightX = 14 + summaryHalfWidth + 6;
    const termsAlignWithSummaryStart = Math.abs(sumY - summaryWouldStartAt) < 0.01;
    let termsY = termsAlignWithSummaryStart ? sumY + 3.5 : sumY + 5;
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.secondary);
    const titleText = "Terms and condition:";
    doc.text(titleText, rightX, termsY);
    
    const titleWidth = doc.getTextWidth(titleText);
    doc.setDrawColor(...colors.secondary);
    doc.setLineWidth(0.3);
    doc.line(rightX, termsY + 1.2, rightX + titleWidth, termsY + 1.2);

    termsY += 6;
    
    const terms = [
      { key: "Interest Rate: ", val: `${interestVal}% p.a.` },
      { key: "Interest Payment: ", val: "Pay at least once every 30 days" },
      { key: "Loan Tenure: ", val: `${loan.tenure || "—"} days (Due by ${formatDateDDMMYYYY(loan.endDate) || "—"})` },
      { key: "Late Penalty: ", val: "Additional 2% p.a. on overdue amount" },
      { key: "Gold Release: ", val: "Within same day or up to 7 working days after closure" }
    ];

    const maxValWidth = summaryHalfWidth - 12;

    terms.forEach(term => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...colors.textPrimary);
      doc.text("•", rightX, termsY);
      
      const keyWidth = doc.getTextWidth(term.key);
      doc.text(term.key, rightX + 4, termsY);
      
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.textSecondary);
      
      const splitVal = doc.splitTextToSize(term.val, maxValWidth - keyWidth - 4);
      doc.text(splitVal, rightX + 4 + keyWidth, termsY);
      
      termsY += splitVal.length * 4;
    });

    // --- Signatures ---
    const sigY = pageH - 25;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.textPrimary);

    // Left padding 10 units for Customer Signature
    doc.text("_______________________", 14, sigY - 4);
    doc.text("Customer Signature", 14 + 5, sigY + 2);

    doc.text("_______________________", pageW - 14, sigY - 4, { align: "right" });
    doc.text("Manager Signature", pageW - 14 - 5, sigY + 2, { align: "right" });

    // --- Footer ---
    doc.setFontSize(9);
    doc.setTextColor(...colors.textPrimary);
    const footerText = "Thank you for your business. For any queries, please contact the branch.";
    doc.text(footerText, pageW / 2, pageH - 10, { align: "center" });
  };

  const renderRulesPage = () => {
    try {
      doc.addPage();
      doc.addImage(Rules1, "PNG", 0, 0, pageW, pageH);
      doc.addPage();
      doc.addImage(Rules2, "PNG", 0, 0, pageW, pageH);
      doc.addPage();
      doc.addImage(Rules3, "PNG", 0, 0, pageW, pageH);
    } catch (e) {
      console.error("Rules image error", e);
    }
  };

  const renderCustomerRulesPage = () => {
    try {
      doc.addPage();
      doc.addImage(CustomerRules, "JPEG", 0, 0, pageW, pageH);
    } catch (e) {
      console.error("Customer rules image error", e);
    }
  };

  renderPage("OFFICE COPY", true);
  renderRulesPage();
  renderPage("CUSTOMER COPY", false);
  renderCustomerRulesPage();

  doc.save(`MAPS-Invoice-${billingId}.pdf`);
}